import { Card as CardType } from '../types';

export interface CartItem {
  card: CardType;
  quantity: number;
}

export interface CartResponse {
  success: boolean;
  cart: CartItem[];
  message?: string;
}

class CartService {
  private baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.goatcards.shop';

  // Generate a unique cart ID for this session
  private getCartId(): string {
    let cartId = localStorage.getItem('cartId');
    if (!cartId) {
      cartId = 'cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('cartId', cartId);
    }
    return cartId;
  }

  // Enhanced localStorage with sessionStorage backup
  private saveToStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.warn('Failed to save to localStorage, using sessionStorage:', error);
      sessionStorage.setItem(key, value);
    }
  }

  private getFromStorage(key: string): string | null {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch (error) {
      console.warn('Failed to read from localStorage, trying sessionStorage:', error);
      return sessionStorage.getItem(key);
    }
  }

  // Save cart to server (with localStorage backup)
  async saveCart(cartItems: CartItem[]): Promise<CartResponse> {
    try {
      // Always save to enhanced storage as backup
      this.saveToStorage('cart', JSON.stringify(cartItems));
      this.saveToStorage('cartTimestamp', Date.now().toString());
      
      // Try to save to server (will fail until cart API is implemented)
      const cartId = this.getCartId();
      const response = await fetch(`${this.baseUrl}/api/v1/cart/${cartId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: cartItems }),
      });

      if (!response.ok) {
        throw new Error('Cart API not implemented yet');
      }

      return await response.json();
    } catch (error) {
      // This is expected until cart API is implemented
      console.log('Using localStorage for cart persistence (server API not available yet)');
      return { success: true, cart: cartItems };
    }
  }

  // Load cart from server (with localStorage fallback)
  async loadCart(): Promise<CartItem[]> {
    try {
      // First try to load from enhanced storage (faster and more reliable for now)
      const savedCart = this.getFromStorage('cart');
      
      if (savedCart) {
        try {
          const cartItems = JSON.parse(savedCart);
          console.log('Loaded cart from localStorage:', cartItems.length, 'items');
          return cartItems;
        } catch (parseError) {
          console.error('Failed to parse cart from localStorage:', parseError);
          localStorage.removeItem('cart');
          localStorage.removeItem('cartTimestamp');
        }
      }

      // Try to load from server (will fail until cart API is implemented)
      const cartId = this.getCartId();
      const response = await fetch(`${this.baseUrl}/api/v1/cart/${cartId}`);

      if (!response.ok) {
        throw new Error('Cart API not implemented yet');
      }

      const data = await response.json();
      return data.cart || [];
    } catch (error) {
      // This is expected until cart API is implemented
      console.log('Using localStorage for cart loading (server API not available yet)');
      return [];
    }
  }

  // Clear cart from server (with localStorage backup)
  async clearCart(): Promise<CartResponse> {
    try {
      // Always clear enhanced storage
      localStorage.removeItem('cart');
      localStorage.removeItem('cartTimestamp');
      sessionStorage.removeItem('cart');
      sessionStorage.removeItem('cartTimestamp');
      
      // Try to clear from server (will fail until cart API is implemented)
      const cartId = this.getCartId();
      const response = await fetch(`${this.baseUrl}/api/v1/cart/${cartId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Cart API not implemented yet');
      }

      return await response.json();
    } catch (error) {
      // This is expected until cart API is implemented
      console.log('Cleared cart from localStorage (server API not available yet)');
      return { success: true, cart: [] };
    }
  }

  // Sync cart with server (called periodically)
  async syncCart(cartItems: CartItem[]): Promise<void> {
    try {
      await this.saveCart(cartItems);
    } catch (error) {
      console.warn('Cart sync failed:', error);
    }
  }
}

export const cartService = new CartService();
